import React, { useRef, useState } from 'react';
import { Parameter } from '@shared/types';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  validateParameterValue,
  isMeasurementParameter,
  getParameterIcon,
  calculateParameterRange,
  calculateParameterStep,
} from '@/utils/parameterUtils';
import { ParameterSlider } from '@/components/parameter/ParameterSlider';
import { ColorInput } from '@/components/parameter/ColorInput';
import { ColorSwatchPicker } from '@/components/parameter/ColorSwatchPicker';
import { MarkdownBlock } from '@/components/parameter/MarkdownBlock';
import { CameraPiPBlock } from '@/components/parameter/CameraPiPBlock';
import { SvgGroupInput } from '@/components/parameter/SvgGroupInput';
import type { PipCameraConfig } from '@/utils/parameterUtils';
import { MaterialInput } from '@/components/parameter/MaterialInput';
import { RangeInput } from '@/components/parameter/RangeInput';
import { FontInput } from '@/components/parameter/FontInput';
import { StepperInput } from '@/components/parameter/StepperInput';
import { XYPadInput } from '@/components/parameter/XYPadInput';
import { XYZPadInput } from '@/components/parameter/XYZPadInput';
import { PolygonPadInput } from '@/components/parameter/PolygonPadInput';
import { AngleInput } from '@/components/parameter/AngleInput';
import { GridInput } from '@/components/parameter/GridInput';
import { StackInput } from '@/components/parameter/StackInput';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';
import { CodeInput } from '@/components/parameter/CodeInput';
import { scadToHex, scadToAlpha, hexToScadWithAlpha } from '@/utils/colorUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Lock, LockOpen, Minus, Plus, Upload, type LucideIcon } from 'lucide-react';
import {
  getFileType,
  isBinaryFileType,
  type ProjectFile,
} from '@/components/files/FileManager';

/** Small lock toggle rendered inside the label column of a parameter row. */
function LockButton({
  isLocked,
  onToggle,
}: {
  isLocked: boolean;
  onToggle: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={`ml-auto flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-sm transition-colors ${
            isLocked
              ? 'text-foreground'
              : 'text-muted-foreground/30 hover:text-muted-foreground'
          }`}
          aria-label={isLocked ? 'Unlock parameter' : 'Lock parameter'}
        >
          {isLocked ? (
            <Lock className="h-3 w-3" />
          ) : (
            <LockOpen className="h-3 w-3" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {isLocked
          ? "Locked — won't be randomized"
          : 'Lock to exclude from randomization'}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Wraps the label cell with a lock button aligned to the right.
 * Keeps the existing 96px column width intact.
 */
function LabelCell({
  param,
  IconComponent,
  className,
  isLocked,
  onToggleLock,
  showLock = true,
}: {
  param: Parameter;
  IconComponent: LucideIcon;
  className?: string;
  isLocked: boolean;
  onToggleLock: () => void;
  showLock?: boolean;
}) {
  return (
    <div className="flex items-center gap-0.5 overflow-hidden">
      <ParameterLabel
        param={param}
        IconComponent={IconComponent}
        className={className}
      />
      {showLock && <LockButton isLocked={isLocked} onToggle={onToggleLock} />}
    </div>
  );
}

// Maximum total character length for all option labels to use toggle group
const MAX_TOGGLE_GROUP_TOTAL_CHARS = 24;
const EMPTY_PROJECT_FILES: ProjectFile[] = [];
// Maximum number of options to show as toggle group
const MAX_TOGGLE_GROUP_OPTIONS = 3;

// Check if a label references an SVG file (e.g., "stripes.svg")
function isSvgFileLabel(label: string): boolean {
  return label.toLowerCase().endsWith('.svg');
}

// Check if all options reference SVG files
function hasAllSvgOptions(
  options: { label: string; value: string | number }[],
): boolean {
  return (
    options.length >= 2 && options.every((opt) => isSvgFileLabel(opt.label))
  );
}

// Get SVG content from project files by filename
function getSvgContent(
  filename: string,
  projectFiles: ProjectFile[],
): string | null {
  const file = projectFiles.find(
    (f) =>
      f.filename.toLowerCase() === filename.toLowerCase() &&
      f.fileType === 'svg',
  );
  if (file && typeof file.content === 'string') {
    return file.content;
  }
  return null;
}

// Helper to determine if options should be rendered as toggle group
function shouldUseToggleGroup(
  options: { label: string; value: string | number }[],
): boolean {
  if (options.length < 2 || options.length > MAX_TOGGLE_GROUP_OPTIONS) {
    return false;
  }
  const totalChars = options.reduce((sum, opt) => sum + opt.label.length, 0);
  return totalChars <= MAX_TOGGLE_GROUP_TOTAL_CHARS;
}

export function ParameterInput({
  param,
  handleCommit,
  projectFiles = EMPTY_PROJECT_FILES,
  onFileAdd,
  onApplySnapshot,
  onSetParameter,
  isLocked = false,
  onToggleLock,
  showLock = true,
  allParameters = [],
  scadCode = '',
}: {
  param: Parameter;
  handleCommit: (param: Parameter, value: Parameter['value']) => void;
  projectFiles?: ProjectFile[];
  onFileAdd?: (file: ProjectFile) => void;
  onApplySnapshot?: (snapshotId: string) => void;
  onSetParameter?: (name: string, value: string) => void;
  isLocked?: boolean;
  onToggleLock?: () => void;
  /** When false the lock toggle is hidden (e.g. in blog/product embeds where randomization is unavailable) */
  showLock?: boolean;
  /** All parameters in the current SCAD file — forwarded to CodeInput for identifier validation */
  allParameters?: Parameter[];
  /** Raw SCAD source — forwarded to CodeInput for loop-context detection */
  scadCode?: string;
}) {
  // Lazy initializer captures the initial param once; the ref-based check below
  // keeps state in sync when the parent passes a different param reference.
  const [paramState, setParamState] = useState<Parameter>(() => param);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset local state when the incoming param reference changes
  const prevParamRef = useRef(param);
  if (prevParamRef.current !== param) {
    prevParamRef.current = param;
    setParamState(param);
  }

  // Shared lock props passed to every LabelCell
  const lockProps = {
    isLocked,
    onToggleLock: onToggleLock ?? (() => {}),
    showLock,
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const handleValueChange = (value: Parameter['value']) => {
    setParamState((prev) => ({ ...prev, value }));
  };

  const handleValueCommit = (value: Parameter['value']) => {
    setParamState((prev) => ({ ...prev, value }));
    handleCommit(paramState, value);
  };

  // Render Markdown block — read-only, full width, no label or commit
  if (paramState.type === 'markdown') {
    return (
      <div className="w-full">
        <MarkdownBlock
          content={String(paramState.value)}
          variant={paramState.markdownVariant}
          onApplySnapshot={onApplySnapshot}
          onSetParameter={onSetParameter}
        />
      </div>
    );
  }

  // Render camera PiP — read-only, full width, inline 3D view
  if (paramState.type === 'camera_pip') {
    try {
      const config = JSON.parse(String(paramState.value)) as PipCameraConfig;
      return (
        <div className="w-full">
          <CameraPiPBlock config={config} />
        </div>
      );
    } catch {
      return null;
    }
  }

  // Render SVG checkbox/radio group — full-width interactive SVG, no label row
  if (
    paramState.type === 'svgcheckboxgroup' ||
    paramState.type === 'svgradiogroup'
  ) {
    return (
      <div className="w-full">
        <SvgGroupInput
          param={paramState}
          projectFiles={projectFiles}
          onChange={handleValueCommit}
        />
      </div>
    );
  }

  // Render Color Swatch Picker for color params with a limited set of options
  if (
    paramState.type === 'color' &&
    param.options &&
    param.options.length > 0
  ) {
    const IconComponent = getParameterIcon(paramState.name);
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <ColorSwatchPicker
          value={scadToHex(paramState.value)}
          options={param.options}
          onChange={(hex) => handleValueCommit(hex)}
        />
      </div>
    );
  }

  // Render Grid — must come before the string[] fallback
  if (paramState.type === 'grid') {
    return <GridInput param={paramState} onCommit={handleValueCommit} />;
  }

  // Render Stack — must come before the string[] fallback
  if (paramState.type === 'stack') {
    return <StackInput param={paramState} onCommit={handleValueCommit} />;
  }

  // Render Stepper — must come before the options block because the stepper
  // parser also populates param.options (for the value list) and would
  // otherwise be caught by the generic dropdown/toggle-group branch below.
  if (paramState.type === 'stepper') {
    return <StepperInput param={paramState} onCommit={handleValueCommit} />;
  }

  // Render Toggle Group for 2-4 short options, otherwise use Select
  // Note: 'code' parameters with options use CodeInput's combobox instead.
  if (param.options && param.options.length > 0 && param.type !== 'code') {
    const IconComponent = getParameterIcon(paramState.name);
    const useToggleGroup = shouldUseToggleGroup(param.options);
    const useSvgToggleGroup =
      hasAllSvgOptions(param.options) &&
      param.options.length <= MAX_TOGGLE_GROUP_OPTIONS;

    // SVG visual toggle group
    if (useSvgToggleGroup) {
      return (
        <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
          <LabelCell
            param={paramState}
            IconComponent={IconComponent}
            {...lockProps}
          />
          <ToggleGroup
            type="single"
            value={String(paramState.value)}
            onValueChange={(value) => {
              if (value) {
                const numVal = Number(value);
                const finalValue = isNaN(numVal) ? value : numVal;
                handleValueCommit(finalValue);
              }
            }}
            className="justify-start"
          >
            {param.options.map((opt) => {
              const svgContent = getSvgContent(opt.label, projectFiles);
              const displayName = opt.label.replace(/\.svg$/i, '');
              return (
                <Tooltip key={String(opt.value)}>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem
                      value={String(opt.value)}
                      className="h-9 w-9 p-1.5 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    >
                      {svgContent ? (
                        <div
                          className="h-full w-full [&>svg]:h-full [&>svg]:w-full [&>svg]:object-contain"
                          dangerouslySetInnerHTML={{ __html: svgContent }}
                        />
                      ) : (
                        <span className="text-[10px]">
                          {displayName.slice(0, 3)}
                        </span>
                      )}
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent side="top">{displayName}</TooltipContent>
                </Tooltip>
              );
            })}
          </ToggleGroup>
        </div>
      );
    }

    // Text toggle group for short options
    if (useToggleGroup) {
      return (
        <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
          <LabelCell
            param={paramState}
            IconComponent={IconComponent}
            {...lockProps}
          />
          <ToggleGroup
            type="single"
            value={String(paramState.value)}
            onValueChange={(value) => {
              if (value) {
                const numVal = Number(value);
                const finalValue = isNaN(numVal) ? value : numVal;
                handleValueCommit(finalValue);
              }
            }}
            className="justify-start"
          >
            {param.options.map((opt) => (
              <ToggleGroupItem
                key={String(opt.value)}
                value={String(opt.value)}
                className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      );
    }

    // Select dropdown for many options
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <Select
          value={String(paramState.value)}
          onValueChange={(value) => {
            const numVal = Number(value);
            const finalValue = isNaN(numVal) ? value : numVal;
            handleValueCommit(finalValue);
          }}
        >
          <SelectTrigger className="h-8 w-full text-xs">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {param.options.map((opt) => (
              <SelectItem
                key={String(opt.value)}
                value={String(opt.value)}
                className="text-xs"
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Render File Upload
  if (paramState.type === 'file') {
    const IconComponent = getParameterIcon(paramState.name);
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <div className="flex w-full items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              const fileType = getFileType(file.name);
              const isBinary = isBinaryFileType(fileType);

              let content: string | ArrayBuffer;
              if (isBinary) {
                content = await file.arrayBuffer();
              } else {
                content = await file.text();
              }

              const projectFile: ProjectFile = {
                id: crypto.randomUUID(),
                filename: file.name,
                fileType,
                content,
                size: file.size,
                isBinary,
              };

              onFileAdd?.(projectFile);
              handleValueCommit(file.name);

              // Reset input
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            className="h-7 w-full justify-start gap-2 px-2 text-xs font-normal"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {String(paramState.value) || 'Upload file'}
            </span>
          </Button>
        </div>
      </div>
    );
  }

  // Render Color Picker with alpha support
  if (paramState.type === 'color') {
    const IconComponent = getParameterIcon(paramState.name);
    const currentAlpha = scadToAlpha(paramState.value);
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <ColorInput
          value={scadToHex(paramState.value)}
          onChange={(hex) =>
            handleValueCommit(hexToScadWithAlpha(hex, currentAlpha))
          }
          alpha={currentAlpha}
          onAlphaChange={(a) =>
            handleValueCommit(
              hexToScadWithAlpha(scadToHex(paramState.value), a),
            )
          }
          compact
        />
      </div>
    );
  }

  // Render Material Editor
  if (paramState.type === 'material') {
    const IconComponent = getParameterIcon(paramState.name);
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          className="pt-2"
          {...lockProps}
        />
        <MaterialInput
          value={paramState.value as number[]}
          onChange={(v) => handleValueCommit(v)}
        />
      </div>
    );
  }

  // Render Range Input
  if (paramState.type === 'range') {
    return <RangeInput param={paramState} onCommit={handleValueCommit} />;
  }

  // Render Font Picker
  if (paramState.type === 'font') {
    const IconComponent = getParameterIcon(paramState.name);
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <FontInput
          value={String(paramState.value)}
          onChange={(font) => handleValueCommit(font)}
        />
      </div>
    );
  }

  // Render Polygon Pad
  if (paramState.type === 'polygonpad') {
    return <PolygonPadInput param={paramState} onCommit={handleValueCommit} />;
  }

  // Render XY Pad
  if (paramState.type === 'xypad') {
    return <XYPadInput param={paramState} onCommit={handleValueCommit} />;
  }

  // Render XYZ Pad
  if (paramState.type === 'xyzpad') {
    return <XYZPadInput param={paramState} onCommit={handleValueCommit} />;
  }

  // Render Angle Dial — must come before the number slider catch-all
  if (paramState.type === 'angle') {
    return <AngleInput param={paramState} onCommit={handleValueCommit} />;
  }

  if (!paramState.type || paramState.type === 'number') {
    const IconComponent = getParameterIcon(paramState.name);
    const { min: numMin, max: numMax } = calculateParameterRange(paramState);
    const numStep = calculateParameterStep(paramState);
    const currentNumValue = Number(paramState.value);
    const handleStep = (direction: 1 | -1) => {
      const next = Math.round((currentNumValue + direction * numStep) / numStep) * numStep;
      const clamped = Math.min(numMax, Math.max(numMin, next));
      handleValueChange(clamped);
      handleValueCommit(clamped);
    };
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <div className="flex w-full items-center gap-3">
          <ParameterSlider
            param={paramState}
            onValueChange={handleValueChange}
            onValueCommit={handleValueCommit}
          />
          <div className="flex flex-shrink-0 items-center gap-1">
            <Input
              id={paramState.name}
              name={paramState.name}
              autoComplete="off"
              className="h-7 w-14 rounded-md px-2 text-center text-xs"
              value={String(paramState.value)}
              onKeyDown={onEnter}
              onChange={(e) => handleValueChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                handleValueCommit(
                  validateParameterValue(paramState, paramState.value),
                );
              }}
            />
            <div className="flex flex-col">
              <button
                className="flex h-3.5 w-5 items-center justify-center rounded-t border border-b-0 border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                onClick={() => handleStep(1)}
                disabled={currentNumValue >= numMax}
                tabIndex={-1}
                aria-label="Increase value"
              >
                <Plus className="h-2.5 w-2.5" />
              </button>
              <button
                className="flex h-3.5 w-5 items-center justify-center rounded-b border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                onClick={() => handleStep(-1)}
                disabled={currentNumValue <= numMin}
                tabIndex={-1}
                aria-label="Decrease value"
              >
                <Minus className="h-2.5 w-2.5" />
              </button>
            </div>
            {(() => {
              const unitLabel = paramState.unit !== undefined
                ? paramState.unit
                : isMeasurementParameter(paramState) ? 'mm' : '';
              return unitLabel ? (
                <span className="text-left text-xs text-muted-foreground">{unitLabel}</span>
              ) : null;
            })()}
          </div>
        </div>
      </div>
    );
  }
  if (param.type === 'boolean') {
    const IconComponent = getParameterIcon(paramState.name);
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <Switch
          id={paramState.name}
          name={paramState.name}
          checked={Boolean(paramState.value)}
          onCheckedChange={(checked) => handleValueCommit(checked)}
        />
      </div>
    );
  }
  if (param.type === 'code') {
    const IconComponent = getParameterIcon(paramState.name);
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <CodeInput
          param={paramState}
          allParameters={allParameters}
          scadCode={scadCode}
          onCommit={(value) => handleValueCommit(value)}
          onChange={(value) => handleValueChange(value)}
        />
      </div>
    );
  }
  if (param.type === 'string') {
    const IconComponent = getParameterIcon(paramState.name);
    if (param.multiline) {
      return (
        <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
          <LabelCell
            param={paramState}
            IconComponent={IconComponent}
            {...lockProps}
          />
          <Textarea
            id={paramState.name}
            name={paramState.name}
            autoComplete="off"
            className="min-h-[72px] w-full min-w-0 rounded-md px-2 py-1.5 text-xs"
            value={String(paramState.value)}
            onChange={(e) => handleValueChange(e.target.value)}
            onBlur={() => handleValueCommit(paramState.value)}
          />
        </div>
      );
    }
    return (
      <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
        <LabelCell
          param={paramState}
          IconComponent={IconComponent}
          {...lockProps}
        />
        <Input
          id={paramState.name}
          name={paramState.name}
          autoComplete="off"
          className="h-7 w-full min-w-0 rounded-md px-2 text-xs"
          value={String(paramState.value)}
          onChange={(e) => handleValueChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => handleValueCommit(paramState.value)}
          onKeyDown={onEnter}
        />
      </div>
    );
  }
  if (paramState.type === 'string[]') {
    if (Array.isArray(paramState.value)) {
      const IconComponent = getParameterIcon(paramState.name);
      return (
        <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
          <LabelCell
            param={paramState}
            IconComponent={IconComponent}
            className="pt-2"
            {...lockProps}
          />
          <div className="flex w-full flex-col gap-2">
            {paramState.value.map((value, index) => (
              <Input
                key={`${paramState.name}-${index}`}
                id={`${paramState.name}-${index}`}
                name={paramState.name}
                autoComplete="off"
                className="h-7 w-full min-w-0 rounded-md px-2 text-xs"
                value={String(value)}
                onKeyDown={onEnter}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  handleValueChange(
                    (paramState.value as string[]).map(
                      (itemValue, itemIndex) =>
                        itemIndex === index ? e.target.value : itemValue,
                    ),
                  )
                }
                onBlur={(e) =>
                  handleValueCommit(
                    (paramState.value as string[]).map(
                      (itemValue, itemIndex) =>
                        itemIndex === index ? e.target.value : itemValue,
                    ),
                  )
                }
              />
            ))}
          </div>
        </div>
      );
    }
  }
  if (paramState.type === 'number[]') {
    if (Array.isArray(paramState.value)) {
      const IconComponent = getParameterIcon(paramState.name);
      return (
        <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
          <LabelCell
            param={paramState}
            IconComponent={IconComponent}
            className="pt-2"
            {...lockProps}
          />
          <div className="flex w-full flex-col gap-2">
            {paramState.value.map((value, index) => {
              const itemParam = {
                ...paramState,
                defaultValue: Array.isArray(paramState.defaultValue)
                  ? paramState.defaultValue[index]
                  : paramState.defaultValue,
                value: value,
              };

              return (
                <div
                  key={`${paramState.name}-${index}`}
                  className="flex w-full items-center gap-3"
                >
                  {paramState.vectorLabels &&
                    paramState.vectorLabels[index] && (
                      <span className="w-4 shrink-0 text-center text-xs font-medium text-muted-foreground">
                        {paramState.vectorLabels[index]}
                      </span>
                    )}
                  <ParameterSlider
                    param={itemParam}
                    onValueChange={(newValue) =>
                      handleValueChange(
                        (paramState.value as number[]).map(
                          (itemValue, itemIndex) =>
                            itemIndex === index ? newValue : itemValue,
                        ),
                      )
                    }
                    onValueCommit={(newValue) =>
                      handleValueCommit(
                        (paramState.value as number[]).map(
                          (itemValue, itemIndex) =>
                            itemIndex === index ? newValue : itemValue,
                        ),
                      )
                    }
                  />
                  <div className="flex items-center gap-1">
                    <Input
                      id={`${paramState.name}-${index}`}
                      name={paramState.name}
                      autoComplete="off"
                      className="h-7 w-14 rounded-md px-2 text-center text-xs"
                      value={String(value)}
                      onKeyDown={onEnter}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        handleValueChange(
                          (paramState.value as number[]).map(
                            (itemValue, itemIndex) =>
                              itemIndex === index
                                ? (e.target.value as unknown as number)
                                : itemValue,
                          ),
                        )
                      }
                      onBlur={(e) => {
                        const updatedValue = (paramState.value as number[]).map(
                          (itemValue, itemIndex) => {
                            if (itemIndex === index) {
                              return validateParameterValue(
                                itemParam,
                                e.target.value,
                              );
                            }
                            return itemValue;
                          },
                        );
                        handleValueCommit(updatedValue as Parameter['value']);
                      }}
                    />
                    <div className="flex flex-col">
                      <button
                        className="flex h-3.5 w-5 items-center justify-center rounded-t border border-b-0 border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                        tabIndex={-1}
                        aria-label="Increase value"
                        disabled={Number(value) >= calculateParameterRange(itemParam).max}
                        onClick={() => {
                          const { min: iMin, max: iMax } = calculateParameterRange(itemParam);
                          const iStep = calculateParameterStep(itemParam);
                          const cur = Number(value);
                          const next = Math.min(iMax, Math.max(iMin, Math.round((cur + iStep) / iStep) * iStep));
                          const updated = (paramState.value as number[]).map((v, i) => i === index ? next : v);
                          handleValueChange(updated);
                          handleValueCommit(updated as Parameter['value']);
                        }}
                      >
                        <Plus className="h-2.5 w-2.5" />
                      </button>
                      <button
                        className="flex h-3.5 w-5 items-center justify-center rounded-b border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                        tabIndex={-1}
                        aria-label="Decrease value"
                        disabled={Number(value) <= calculateParameterRange(itemParam).min}
                        onClick={() => {
                          const { min: iMin, max: iMax } = calculateParameterRange(itemParam);
                          const iStep = calculateParameterStep(itemParam);
                          const cur = Number(value);
                          const next = Math.min(iMax, Math.max(iMin, Math.round((cur - iStep) / iStep) * iStep));
                          const updated = (paramState.value as number[]).map((v, i) => i === index ? next : v);
                          handleValueChange(updated);
                          handleValueCommit(updated as Parameter['value']);
                        }}
                      >
                        <Minus className="h-2.5 w-2.5" />
                      </button>
                    </div>
                    {(() => {
                      const unitLabel = paramState.unit !== undefined
                        ? paramState.unit
                        : isMeasurementParameter(paramState) ? 'mm' : '';
                      return unitLabel ? (
                        <span className="text-left text-xs text-muted-foreground">{unitLabel}</span>
                      ) : null;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }
  if (paramState.type === 'boolean[]') {
    if (Array.isArray(paramState.value)) {
      const IconComponent = getParameterIcon(paramState.name);
      return (
        <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
          <LabelCell
            param={paramState}
            IconComponent={IconComponent}
            className="pt-2"
            {...lockProps}
          />
          <div className="flex w-full flex-col gap-2">
            {paramState.value.map((value, index) => (
              <Switch
                key={`${paramState.name}-${index}`}
                id={`${paramState.name}-${index}`}
                name={paramState.name}
                checked={Boolean(value)}
                onCheckedChange={(checked) =>
                  handleValueCommit(
                    (paramState.value as boolean[]).map(
                      (itemValue, itemIndex) =>
                        itemIndex === index ? checked : itemValue,
                    ),
                  )
                }
              />
            ))}
          </div>
        </div>
      );
    }
  }
}
